/**
 * ChatApp - Main Chat-First Interface
 * Claude-like UI for Trendy AI Assistant
 */

import { useState, useRef, useEffect, useCallback, memo } from 'react'
import {
  MessageSquarePlus,
  Gauge,
  Search,
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Send,
  Paperclip,
  StopCircle,
  Sparkles,
  Menu,
  X,
  MoreHorizontal,
  Pencil,
  Trash2,
  Copy,
  Check,
  PanelLeftClose,
  Maximize2,
  Command,
  Sun,
  Moon,
  Monitor,
  MessageCircle,
  BookOpen,
  Blocks,
  Plug,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react'
import useTheme from '../hooks/useTheme'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSessions } from '../hooks/useSessions'
import { chat, chatStream } from '../lib/api'
import MessageContent from '../components/chat/MessageContent'
import TimingDisplay from '../components/chat/TimingDisplay'
import M365Button from '../components/M365Button'
import ArtifactPanel from '../components/artifacts/ArtifactPanel'
import LandingPage from '../components/landing/LandingWidgets'
import SearchModal from '../components/chat/SearchModal'
import PlaybooksTab from '../components/sidebar/PlaybooksTab'
import ModulesTab from '../components/sidebar/ModulesTab'
import ConnectorsTab from '../components/sidebar/ConnectorsTab'
import ProgressIndicator from '../components/chat/ProgressIndicator'
import WriteApprovalModal from '../components/chat/WriteApprovalModal'
import SourcePane from '../components/chat/SourcePane'
import { SourcePaneProvider, useSourcePane } from '../contexts/SourcePaneContext'
import { CitationChipRow } from '../components/chat/CitationChip'
import { parseCitationsSafe, parseActionCardSafe } from '../types/cards'
import ErrorBoundary from '../components/ErrorBoundary'
import { getModuleLabel, getModuleColor } from '../lib/tool-modules'

// =============================================================================
// Artifact Persistence Helpers (supports multiple artifacts per session)
// =============================================================================
const ARTIFACT_STORAGE_KEY = 'trendy_session_artifacts'
const MAX_STORED_SESSIONS = 50 // Limit sessions to prevent localStorage bloat
const MAX_ARTIFACTS_PER_SESSION = 10 // Limit artifacts per session

/**
 * Get all stored artifacts from localStorage
 */
function getStoredArtifacts() {
  try {
    const stored = localStorage.getItem(ARTIFACT_STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch (err) {
    console.error('Failed to load artifacts from storage:', err)
    return {}
  }
}

/**
 * Get artifact label for display in tabs
 */
function getArtifactLabel(artifact) {
  if (!artifact) return 'Artifact'

  switch (artifact.type) {
    case 'photo_gallery':
    case 'photo_gallery_urls':
      return `Photos ${artifact.order_num || ''}`
    case 'customer_360':
      return `Customer ${artifact.customer?.name?.substring(0, 12) || artifact.cust_num || ''}`
    case 'quote_detail':
      return `Quote ${artifact.quote_num || ''}`
    case 'order_detail':
      return `Order ${artifact.order_num || ''}`
    case 'job_detail':
      return `Job ${artifact.job_num || ''}`
    case 'part_detail':
      return `Part ${artifact.part_num || ''}`
    case 'invoice_detail':
      return `Invoice ${artifact.invoice_num || ''}`
    case 'po_detail':
      return `PO ${artifact.po_num || ''}`
    case 'ap_invoice_detail':
      return `AP Invoice ${artifact.invoice_num || ''}`
    case 'shipment_detail':
      return `Shipment ${artifact.pack_num || ''}`
    case 'attachment_viewer':
      return artifact.metadata?.filename?.substring(0, 15) || 'Attachment'
    case 'attachment_list':
      return artifact.title?.substring(0, 15) || 'Attachments'
    case 'generated_document':
      return artifact.filename?.replace(/_/g, ' ')?.replace('.pdf', '')?.substring(0, 20) || 'Document'
    default:
      return artifact.type?.replace(/_/g, ' ')?.substring(0, 15) || 'Artifact'
  }
}

/**
 * Save artifacts array for a session
 */
function saveArtifactsForSession(sessionId, artifactsArray, activeIndex) {
  if (!sessionId) return

  try {
    const allStoredArtifacts = getStoredArtifacts()
    allStoredArtifacts[sessionId] = {
      artifacts: artifactsArray.slice(-MAX_ARTIFACTS_PER_SESSION), // Keep only last N
      activeIndex: activeIndex,
      savedAt: new Date().toISOString()
    }

    // Prune old sessions if we have too many
    const sessionIds = Object.keys(allStoredArtifacts)
    if (sessionIds.length > MAX_STORED_SESSIONS) {
      const sorted = sessionIds.sort((a, b) =>
        new Date(allStoredArtifacts[b].savedAt) - new Date(allStoredArtifacts[a].savedAt)
      )
      sorted.slice(MAX_STORED_SESSIONS).forEach(id => delete allStoredArtifacts[id])
    }

    localStorage.setItem(ARTIFACT_STORAGE_KEY, JSON.stringify(allStoredArtifacts))
  } catch (err) {
    console.error('Failed to save artifacts to storage:', err)
  }
}

/**
 * Get artifacts array for a session
 */
function getArtifactsForSession(sessionId) {
  if (!sessionId) return { artifacts: [], activeIndex: 0 }

  try {
    const allStoredArtifacts = getStoredArtifacts()
    const sessionData = allStoredArtifacts[sessionId]

    // Handle migration from old single-artifact format
    if (sessionData?.data && !sessionData?.artifacts) {
      return { artifacts: [sessionData.data], activeIndex: 0 }
    }

    return {
      artifacts: sessionData?.artifacts || [],
      activeIndex: sessionData?.activeIndex || 0
    }
  } catch (err) {
    console.error('Failed to get artifacts from storage:', err)
    return { artifacts: [], activeIndex: 0 }
  }
}

/**
 * Clear all artifacts for a session
 */
function clearArtifactsForSession(sessionId) {
  if (!sessionId) return

  try {
    const allStoredArtifacts = getStoredArtifacts()
    delete allStoredArtifacts[sessionId]
    localStorage.setItem(ARTIFACT_STORAGE_KEY, JSON.stringify(allStoredArtifacts))
  } catch (err) {
    console.error('Failed to clear artifacts from storage:', err)
  }
}

/**
 * Sidebar Component
 */
const SIDEBAR_TABS = [
  { key: 'chats', label: 'Chats', icon: MessageCircle },
  { key: 'playbooks', label: 'Playbooks', icon: BookOpen },
  { key: 'modules', label: 'Modules', icon: Blocks },
  { key: 'connectors', label: 'Connectors', icon: Plug },
]

function Sidebar({ isOpen, onClose, isLoading, onCancelRequest, isCollapsed, onToggleCollapse, onStartChat }) {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const {
    groupedSessions,
    currentSessionId,
    createSession,
    selectSession,
    deleteSession,
    renameSession,
    searchSessions,
  } = useSessions()
  const [activeTab, setActiveTab] = useState('chats')
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredSessions, setFilteredSessions] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [menuOpenId, setMenuOpenId] = useState(null)

  // Handle search filtering
  useEffect(() => {
    if (searchQuery.trim()) {
      const results = searchSessions(searchQuery)
      setFilteredSessions(results)
    } else {
      setFilteredSessions(null)
    }
  }, [searchQuery, searchSessions])

  // Switch sessions freely — background streams keep running
  const handleNewChat = () => {
    createSession()
    onClose?.()
  }

  const handleSelectSession = (sessionId) => {
    if (sessionId === currentSessionId) return // Already on this session
    selectSession(sessionId)
    onClose?.()
  }

  const handleStartRename = (session) => {
    setEditingId(session.id)
    setEditTitle(session.title)
    setMenuOpenId(null)
  }

  const handleSaveRename = (sessionId) => {
    if (editTitle.trim()) {
      renameSession(sessionId, editTitle.trim())
    }
    setEditingId(null)
    setEditTitle('')
  }

  const handleDelete = (sessionId) => {
    deleteSession(sessionId)
    setMenuOpenId(null)
  }

  const SessionItem = ({ session }) => {
    const isActive = session.id === currentSessionId
    const isEditing = editingId === session.id
    const isMenuOpen = menuOpenId === session.id

    return (
      <div
        className={`group relative flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
          isActive
            ? 'bg-gray-200 dark:bg-surface-dark-overlay'
            : 'hover:bg-gray-100 dark:hover:bg-surface-dark-raised'
        }`}
        onClick={() => !isEditing && handleSelectSession(session.id)}
      >
        {isEditing ? (
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={() => handleSaveRename(session.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveRename(session.id)
              if (e.key === 'Escape') {
                setEditingId(null)
                setEditTitle('')
              }
            }}
            className="flex-1 text-sm bg-white dark:bg-surface-dark-raised border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amtrend-blue"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">
              {session.title}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpenId(isMenuOpen ? null : session.id)
              }}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay rounded transition-opacity"
            >
              <MoreHorizontal size={14} className="text-gray-500 dark:text-gray-400" />
            </button>
          </>
        )}

        {/* Context Menu */}
        {isMenuOpen && (
          <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-surface-dark-raised rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[140px]">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleStartRename(session)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay"
            >
              <Pencil size={14} />
              Rename
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(session.id)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}
      </div>
    )
  }

  const SessionGroup = ({ title, sessions }) => {
    if (sessions.length === 0) return null

    return (
      <div className="mb-4">
        <h3 className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {title}
        </h3>
        <div className="space-y-1">
          {sessions.map(session => (
            <SessionItem key={session.id} session={session} />
          ))}
        </div>
      </div>
    )
  }

  // Collapsed state (desktop only) - just show a thin bar with expand button
  if (isCollapsed) {
    return (
      <div className="hidden lg:flex w-12 bg-gray-50 dark:bg-surface-dark-raised border-r border-gray-200 dark:border-gray-800 flex-col items-center py-4">
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay rounded-lg transition-colors mb-4"
          title="Expand sidebar"
        >
          <ChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
        <button
          onClick={handleNewChat}
          className="p-2 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay rounded-lg transition-colors"
          title="New chat"
        >
          <MessageSquarePlus size={20} className="text-gray-500 dark:text-gray-400" />
        </button>
      </div>
    )
  }

  return (
    <>
    {/* Mobile backdrop — tap to close sidebar */}
    {isOpen && (
      <div
        className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden transition-opacity"
        onClick={onClose}
        aria-label="Close sidebar"
      />
    )}
    <div
      className={`fixed inset-y-0 left-0 z-40 w-72 bg-gray-50 dark:bg-surface-dark border-r border-gray-200 dark:border-gray-800 flex flex-col transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:relative lg:translate-x-0`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <img
              src="/amtrend-logo.png"
              alt="Amtrend"
              className="h-7"
            />
          </div>
          <div className="flex items-center gap-1">
            {/* Collapse button - desktop only */}
            <button
              onClick={onToggleCollapse}
              className="hidden lg:block p-1 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay rounded"
              title="Collapse sidebar"
            >
              <PanelLeftClose size={20} className="text-gray-500 dark:text-gray-400" />
            </button>
            {/* Close button - mobile only */}
            <button
              onClick={onClose}
              className="lg:hidden p-1 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay rounded"
            >
              <X size={20} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <button
          onClick={handleNewChat}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-surface-dark-raised border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay transition-colors"
        >
          <MessageSquarePlus size={18} />
          New chat
        </button>

        <button
          onClick={() => navigate('/production')}
          className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2.5 bg-amtrend-blue text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
        >
          <Gauge size={18} />
          Runway Monitor
        </button>
      </div>

      {/* Tab Bar */}
      <div className="flex px-3 gap-0.5 border-b border-gray-200 dark:border-gray-800">
        {SIDEBAR_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 py-2.5 text-center text-[11px] font-semibold tracking-wide border-b-2 transition-all duration-150 ${
              activeTab === key
                ? 'text-amtrend-blue border-amtrend-blue'
                : 'text-gray-400 dark:text-gray-500 border-transparent hover:text-gray-600 dark:hover:text-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'chats' && (
          <>
            {/* Search — only in Chats tab */}
            <div className="p-3 pb-0">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amtrend-blue focus:border-transparent"
                />
              </div>
            </div>

            {/* Session List */}
            <div className="px-2 pt-2">
              {filteredSessions ? (
                <>
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Search Results ({filteredSessions.length})
                  </div>
                  {filteredSessions.map((session) => (
                    <SessionItem key={session.id} session={session} />
                  ))}
                  {filteredSessions.length === 0 && (
                    <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No conversations match "{searchQuery}"
                    </div>
                  )}
                </>
              ) : (
                <>
                  <SessionGroup title="Today" sessions={groupedSessions.today} />
                  <SessionGroup title="Yesterday" sessions={groupedSessions.yesterday} />
                  <SessionGroup title="Previous 7 Days" sessions={groupedSessions.lastWeek} />
                  <SessionGroup title="Previous 30 Days" sessions={groupedSessions.lastMonth} />
                  <SessionGroup title="Older" sessions={groupedSessions.older} />

                  {Object.values(groupedSessions).every(g => g.length === 0) && (
                    <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No conversations yet.
                      <br />
                      Start a new chat!
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {activeTab === 'playbooks' && (
          <PlaybooksTab onStartChat={onStartChat} userRole={user?.role} />
        )}

        {activeTab === 'modules' && (
          <ModulesTab onStartChat={onStartChat} />
        )}

        {activeTab === 'connectors' && (
          <ConnectorsTab />
        )}
      </div>

      {/* M365 Connection */}
      <div className="px-3 pb-2">
        <M365Button />
      </div>

      {/* User Section */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg">
          <div className="w-8 h-8 bg-amtrend-blue rounded-full flex items-center justify-center text-white text-sm font-medium">
            {user?.name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {user?.name || user?.username || 'User'}
            </p>
            {user?.department && (
              <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{user.department}</p>
            )}
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay rounded-lg transition-colors"
            title="Settings"
          >
            <Settings size={16} className="text-gray-500 dark:text-gray-400" />
          </button>
          <button
            onClick={logout}
            className="p-1.5 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay rounded-lg transition-colors"
            title="Logout"
          >
            <LogOut size={16} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

    </div>
    </>
  )
}

/**
 * Chat Message Component — wrapped in React.memo to prevent re-renders
 * during streaming (messages are immutable once created).
 */
const ChatMessage = memo(function ChatMessage({ message, isLast, onSendMessage, sessionId, maxWidthClass = 'max-w-3xl' }) {
  const [copied, setCopied] = useState(false)
  const [feedback, setFeedback] = useState(null) // 'up' | 'down' | null
  const contentRef = useRef(null)
  const isUser = message.role === 'user'
  const timing = message.timing

  const handleCopy = async () => {
    try {
      // ── Strategy: clone the rendered DOM and inline computed styles ──
      // This preserves KPI cards, color-coded text, tables, progress bars,
      // and all rich visual components exactly as rendered by React.
      // Inline styles survive paste into Outlook, Google Docs, etc.
      const container = contentRef.current
      if (!container) throw new Error('No content ref')

      // 1. Clone the live DOM
      const clone = container.cloneNode(true)

      // 2. Walk live + cloned trees in parallel to inline computed styles
      //    IMPORTANT: Do this BEFORE removing elements, so indices stay in sync
      //    This captures Tailwind's resolved values as inline styles
      const STYLE_PROPS = [
        'color', 'backgroundColor', 'fontSize', 'fontWeight', 'fontFamily',
        'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
        'margin', 'marginTop', 'marginBottom',
        'border', 'borderLeft', 'borderBottom', 'borderCollapse',
        'borderRadius', 'textAlign', 'lineHeight', 'verticalAlign',
        'width', 'minWidth', 'maxWidth', 'textTransform', 'letterSpacing',
      ]
      // Properties where default/empty values should be skipped
      const SKIP_VALUES = new Set([
        'normal', 'none', 'auto', 'start', '0px', '0px 0px', 'rgba(0, 0, 0, 0)',
        'transparent', '0px none rgb(0, 0, 0)', 'medium none', '',
      ])

      const liveEls = container.querySelectorAll('*')
      const cloneEls = clone.querySelectorAll('*')
      const len = Math.min(liveEls.length, cloneEls.length)

      for (let i = 0; i < len; i++) {
        const live = liveEls[i]
        const cloned = cloneEls[i]
        if (live.nodeType !== 1) continue

        const cs = window.getComputedStyle(live)
        const parts = []
        for (const prop of STYLE_PROPS) {
          const val = cs[prop]
          if (!val || SKIP_VALUES.has(val)) continue
          // Convert camelCase to kebab-case
          const kebab = prop.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)

          // Darken grayish text to near-black for email readability
          if (prop === 'color') {
            const m = val.match(/rgb\((\d+),\s*(\d+),\s*(\d+)/)
            if (m) {
              const [r, g, b] = [+m[1], +m[2], +m[3]]
              // Gray-ish: all channels close together, mid-range (not already black or a status color)
              const isGray = Math.abs(r - g) < 40 && Math.abs(g - b) < 40 && r > 30 && r < 180
              if (isGray) { parts.push('color:#1a1a1a'); continue }
            }
          }

          parts.push(`${kebab}:${val}`)
        }

        // For grid/flex containers, mark for post-processing
        const display = cs.display
        if (display === 'grid' || display === 'flex') {
          cloned.setAttribute('data-copy-layout', display)
        }

        if (parts.length) cloned.setAttribute('style', parts.join(';'))

        // Strip class and data-* attributes (not needed after inlining)
        cloned.removeAttribute('class')
        const attrs = [...cloned.attributes]
        for (const attr of attrs) {
          if (attr.name.startsWith('data-') && attr.name !== 'data-copy-layout') cloned.removeAttribute(attr.name)
        }
      }

      // 5. Convert grid/flex layouts to HTML tables for Outlook compatibility
      //    Outlook desktop uses Word's HTML engine — only tables render reliably
      //    This is the same technique Mailchimp/email marketing tools use
      clone.querySelectorAll('[data-copy-layout]').forEach(container => {
        const layout = container.getAttribute('data-copy-layout')
        container.removeAttribute('data-copy-layout')

        const children = Array.from(container.children)
        const count = children.length
        if (!count) return

        // Build an HTML table to replace the grid/flex container
        const table = document.createElement('table')
        table.setAttribute('role', 'presentation')
        table.setAttribute('style', 'border-collapse:collapse;width:100%;margin:8px 0')

        if (layout === 'grid') {
          // KPI cards: arrange in rows of up to 4 columns
          const cols = Math.min(count, 4)
          const widthPct = Math.floor(100 / cols)
          let tr = null
          children.forEach((child, i) => {
            if (i % cols === 0) {
              tr = document.createElement('tr')
              table.appendChild(tr)
            }
            const td = document.createElement('td')
            td.setAttribute('style', `width:${widthPct}%;vertical-align:top;padding:4px`)
            td.appendChild(child) // moves the child node into the td
            tr.appendChild(td)
          })
        } else {
          // Flex rows — single row, children side by side
          const tr = document.createElement('tr')
          table.appendChild(tr)
          children.forEach(child => {
            const td = document.createElement('td')
            td.setAttribute('style', 'vertical-align:top;padding:4px')
            td.appendChild(child)
            tr.appendChild(td)
          })
        }

        container.replaceWith(table)
      })

      // 6. Replace SVG/CSS visuals with email-friendly HTML tables
      //    Parse viz JSON from raw markdown, match to [data-viz] elements
      const raw = message.content || ''
      const vizJsons = []
      const vizRegex = /```(?:json)?\s*\n?\s*(\{[\s\S]*?"vizType"[\s\S]*?\})\s*\n?```/g
      let vizMatch
      while ((vizMatch = vizRegex.exec(raw)) !== null) {
        try { vizJsons.push(JSON.parse(vizMatch[1])) } catch { /* skip malformed */ }
      }

      const vizEls = clone.querySelectorAll('[data-viz]')
      const fmtCur = v => typeof v === 'number' ? (Math.abs(v) >= 1000 ? `$${(v/1000).toFixed(1)}K` : `$${v.toLocaleString()}`) : v
      const fmtPct = v => typeof v === 'number' ? `${v.toFixed(1)}%` : v
      const fmtVal = (v, f) => f === 'currency' ? fmtCur(v) : f === 'percent' ? fmtPct(v) : (typeof v === 'number' ? v.toLocaleString() : v)

      vizEls.forEach((vizEl, idx) => {
        const data = vizJsons[idx]
        if (!data) return

        const vizType = data.vizType
        let replacement = null

        if (vizType === 'comparison') {
          // Estimated vs Actual comparison → data table
          const items = data.items || []
          const estLabel = data.estimatedLabel || 'Estimated'
          const actLabel = data.actualLabel || 'Actual'
          const title = data.title || ''
          let rows = items.map(item => {
            const delta = item.estimated ? (((item.actual - item.estimated) / item.estimated) * 100).toFixed(0) : '—'
            const deltaColor = item.actual > item.estimated ? '#dc2626' : '#16a34a'
            return `<tr>
              <td style="padding:6px 12px;border-bottom:1px solid #eee;font-size:13px">${item.label}</td>
              <td style="padding:6px 12px;border-bottom:1px solid #eee;text-align:right;font-size:13px">${fmtVal(item.estimated, data.format || 'currency')}</td>
              <td style="padding:6px 12px;border-bottom:1px solid #eee;text-align:right;font-size:13px">${fmtVal(item.actual, data.format || 'currency')}</td>
              <td style="padding:6px 12px;border-bottom:1px solid #eee;text-align:right;font-size:13px;color:${deltaColor};font-weight:600">${item.actual > item.estimated ? '+' : ''}${delta}%</td>
            </tr>`
          }).join('')
          replacement = `<table style="border-collapse:collapse;width:100%;margin:8px 0">
            ${title ? `<caption style="text-align:left;font-size:12px;font-weight:600;color:#1a1a1a;padding:4px 0">${title}</caption>` : ''}
            <thead><tr style="background:#f5f5f5">
              <th style="padding:6px 12px;text-align:left;font-size:12px;font-weight:600;border-bottom:2px solid #ddd">Category</th>
              <th style="padding:6px 12px;text-align:right;font-size:12px;font-weight:600;border-bottom:2px solid #ddd">${estLabel}</th>
              <th style="padding:6px 12px;text-align:right;font-size:12px;font-weight:600;border-bottom:2px solid #ddd">${actLabel}</th>
              <th style="padding:6px 12px;text-align:right;font-size:12px;font-weight:600;border-bottom:2px solid #ddd">Δ</th>
            </tr></thead><tbody>${rows}</tbody></table>`
        } else if (vizType === 'mini_bar') {
          // Bar chart → simple table
          const items = data.items || data.data || []
          const labelKey = data.labelKey || 'label'
          const valueKey = data.valueKey || 'value'
          const title = data.title || ''
          let rows = items.slice(0, 8).map(item =>
            `<tr><td style="padding:4px 12px;border-bottom:1px solid #eee;font-size:13px">${item[labelKey]}</td>
            <td style="padding:4px 12px;border-bottom:1px solid #eee;text-align:right;font-size:13px;font-weight:600">${fmtVal(item[valueKey], data.format || 'currency')}</td></tr>`
          ).join('')
          replacement = `<table style="border-collapse:collapse;width:100%;margin:8px 0">
            ${title ? `<caption style="text-align:left;font-size:12px;font-weight:600;color:#1a1a1a;padding:4px 0">${title}</caption>` : ''}
            <tbody>${rows}</tbody></table>`
        } else if (vizType === 'gauge') {
          // Gauge → simple text
          const pct = data.max ? ((data.value / data.max) * 100).toFixed(1) : data.value
          replacement = `<div style="padding:8px 12px;border:1px solid #e5e7eb;border-radius:8px;display:inline-block;margin:4px 0">
            <span style="font-size:11px;color:#6b7280;text-transform:uppercase">${data.label || 'Utilization'}</span><br>
            <span style="font-size:20px;font-weight:700;color:#1a1a1a">${fmtVal(pct, 'percent')}</span></div>`
        } else if (vizType === 'progress_bar') {
          const pct = data.max ? ((data.value / data.max) * 100).toFixed(0) : data.value
          replacement = `<div style="padding:6px 12px;border:1px solid #e5e7eb;border-radius:8px;margin:4px 0">
            <span style="font-size:13px;font-weight:500;color:#1a1a1a">${data.label || ''}: <strong>${pct}%</strong></span>
            ${data.detail ? `<br><span style="font-size:11px;color:#6b7280">${data.detail}</span>` : ''}</div>`
        } else if (vizType === 'sparkline') {
          // Sparkline → current value + trend text
          const values = data.values || []
          const current = data.current || values[values.length - 1]
          const first = values[0] || 0
          const trend = current > first ? '▲ Trending up' : current < first ? '▼ Trending down' : '– Stable'
          const trendColor = current > first ? '#16a34a' : current < first ? '#dc2626' : '#6b7280'
          replacement = `<div style="padding:6px 12px;border:1px solid #e5e7eb;border-radius:8px;display:inline-block;margin:4px 0">
            ${data.label ? `<span style="font-size:11px;color:#6b7280">${data.label}</span> ` : ''}
            <span style="font-size:14px;font-weight:700;color:#1a1a1a">${fmtVal(current, data.format || 'currency')}</span>
            <span style="font-size:11px;color:${trendColor};margin-left:4px">${trend}</span></div>`
        } else if (vizType === 'donut') {
          // Donut → table of segments
          const segments = data.segments || []
          let rows = segments.map(seg =>
            `<tr><td style="padding:3px 12px;font-size:13px">${seg.label}</td>
            <td style="padding:3px 12px;text-align:right;font-size:13px;font-weight:600">${fmtVal(seg.value, data.format || 'currency')}</td></tr>`
          ).join('')
          replacement = `<table style="border-collapse:collapse;margin:8px 0">
            ${data.centerLabel ? `<caption style="text-align:left;font-size:12px;font-weight:600;padding:4px 0">${data.centerLabel}: ${data.centerValue || ''}</caption>` : ''}
            <tbody>${rows}</tbody></table>`
        } else if (vizType === 'traffic_light') {
          const icons = { good: '✅', warning: '⚠️', critical: '❌', neutral: '⚪' }
          replacement = `<span style="font-size:13px">${icons[data.status] || '⚪'} <strong>${data.label || ''}</strong>${data.detail ? ` — ${data.detail}` : ''}</span>`
        } else if (vizType === 'delta') {
          const arrow = data.value > 0 ? '▲' : data.value < 0 ? '▼' : '–'
          const color = data.value > 0 ? '#16a34a' : data.value < 0 ? '#dc2626' : '#6b7280'
          replacement = `<span style="font-size:13px;color:${color};font-weight:600">${arrow} ${data.value > 0 ? '+' : ''}${fmtVal(data.value, data.format || 'percent')}</span>
            ${data.label ? `<span style="font-size:12px;color:#6b7280"> ${data.label}</span>` : ''}`
        } else if (vizType === 'countdown') {
          const emoji = data.days <= 0 ? '🔴' : data.days <= 3 ? '🟠' : data.days <= 7 ? '🟡' : '🔵'
          const text = data.days <= 0 ? 'OVERDUE' : `${data.days} day${data.days !== 1 ? 's' : ''} remaining`
          replacement = `<span style="font-size:13px">${emoji} <strong>${text}</strong>${data.label ? ` — ${data.label}` : ''}</span>`
        } else if (vizType === 'status_timeline') {
          // Timeline → inline text: Step1 ✓ → Step2 ✓ → Step3 (active) → Step4
          const icons = { complete: '✅', active: '🔵', error: '❌', pending: '⚪' }
          const steps = (data.steps || []).map(s => `${icons[s.status] || '⚪'} ${s.label}`).join(' → ')
          replacement = `<div style="font-size:13px;padding:6px 0">${data.title ? `<strong>${data.title}</strong><br>` : ''}${steps}</div>`
        }

        if (replacement) {
          const wrapper = document.createElement('div')
          wrapper.innerHTML = replacement
          vizEl.replaceWith(wrapper)
        }
      })

      // 7. Now remove interactive UI elements (after inlining, so indices were in sync)
      clone.querySelectorAll('button').forEach(el => el.remove())
      // Remove all remaining SVGs (already replaced viz with tables, any left are decorative)
      clone.querySelectorAll('svg').forEach(svg => svg.remove())

      // 7. Also inline styles on the root clone element itself
      const rootCs = window.getComputedStyle(container)
      clone.setAttribute('style', [
        'font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif',
        `font-size:${rootCs.fontSize}`,
        `line-height:${rootCs.lineHeight}`,
        `color:${rootCs.color}`,
      ].join(';'))
      clone.removeAttribute('class')

      const html = clone.innerHTML

      // 8. Generate clean plain text from the rendered content
      const plainText = container.innerText || container.textContent || ''

      // 9. Write both HTML and plain text to clipboard
      const htmlBlob = new Blob([`<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6;color:#333;">${html}</div>`], { type: 'text/html' })
      const textBlob = new Blob([plainText], { type: 'text/plain' })
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        })
      ])
    } catch {
      // Fallback for browsers that don't support ClipboardItem
      const text = contentRef.current?.innerText
        || (message.content || '')
          .replace(/```(?:json)?\s*\n?\s*\{[\s\S]*?"vizType"[\s\S]*?\}\s*\n?```/g, '')
          .replace(/\{"type"\s*:\s*"inline"\s*,\s*"vizType"[\s\S]*?\}\s*(?:\n|$)/g, '')
          .replace(/\n{3,}/g, '\n\n')
          .trim()
      navigator.clipboard.writeText(text)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleFeedback = async (rating) => {
    if (feedback) return // already rated
    setFeedback(rating)
    try {
      const token = localStorage.getItem('amtrend_token')
      await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message_id: message.id, session_id: sessionId, rating }),
      })
    } catch (err) {
      console.error('Failed to submit feedback:', err)
    }
  }

  return (
    <div className={`py-4 sm:py-6 ${isUser ? '' : 'bg-gray-50 dark:bg-surface-dark-raised'}`}>
      <div className={`${maxWidthClass} mx-auto px-3 sm:px-4`}>
        <div className="flex gap-2 sm:gap-4">
          {/* Avatar - smaller on mobile */}
          <div className="flex-shrink-0">
            {isUser ? (
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-medium rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium">
                U
              </div>
            ) : (
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-amtrend-blue rounded-lg flex items-center justify-center">
                <span className="text-white text-xs sm:text-sm font-semibold">T</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                {isUser ? 'You' : 'Trendy'}
              </span>
            </div>

            {/* Display attachments if present - responsive grid */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {message.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="relative bg-gray-100 dark:bg-surface-dark-overlay rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
                  >
                    {attachment.isImage && attachment.preview ? (
                      <img
                        src={attachment.preview}
                        alt={attachment.name}
                        className="max-h-32 sm:max-h-48 max-w-[200px] sm:max-w-xs object-contain rounded-lg"
                      />
                    ) : (
                      <div className="h-14 w-20 sm:h-16 sm:w-24 flex flex-col items-center justify-center p-2">
                        <span className="text-xl sm:text-2xl">
                          {attachment.isPDF ? '📄' : attachment.isExcel ? '📊' : attachment.isCSV ? '📋' : '📎'}
                        </span>
                        <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate w-full text-center mt-1">
                          {attachment.name}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Anti-fabrication banner — hoisted ABOVE the answer so an unverified
                value/link is seen BEFORE the (confidently formatted) content, not
                as a footnote under it. Fires when the grounding verifier flagged a
                record-ID or hand-built deep link not traceable to a tool result. */}
            {!isUser && message.groundingAlert && message.groundingAlert.violations?.length > 0 && (
              <div
                role="alert"
                className="mb-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-400"
              >
                <div className="text-sm font-semibold">⚠️ Unverified data — do not fully trust this answer.</div>
                <div className="mt-0.5 text-xs">
                  Some value(s) or link(s) below could not be matched to live Epicor results. Re-query to confirm.
                </div>
                <ul className="mt-1 list-disc pl-4 text-xs space-y-0.5">
                  {message.groundingAlert.violations.map((v, i) => (
                    <li key={i}>{v}</li>
                  ))}
                </ul>
              </div>
            )}

            <div ref={contentRef} className="prose prose-sm max-w-none text-gray-700 dark:text-gray-300 text-sm sm:text-base">
              <MessageContent content={message.content} onSendMessage={onSendMessage} />
            </div>

            {/* Citation chips — PR1: clicking a chip opens its source in the right pane */}
            {!isUser && message.citations && message.citations.length > 0 && (
              <CitationChipRow citations={message.citations} />
            )}

            {/* Tools used — module-level labels.
                Styled as flat metadata text (NOT chips) so they don't read as
                clickable. The chip-shaped variant collided with PR2 citation
                chips and looked interactive when it isn't. */}
            {message.toolsUsed && message.toolsUsed.length > 0 && (
              <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 dark:text-gray-500">
                  Tools
                </span>
                {/* Deduplicate by module label */}
                {[...new Set(message.toolsUsed.map(t => getModuleLabel(t)))].map((label, i, arr) => {
                  const sampleTool = message.toolsUsed.find(t => getModuleLabel(t) === label)
                  const colors = getModuleColor(sampleTool)
                  return (
                    <span key={label} className="inline-flex items-center gap-2">
                      <span className={`text-[10px] sm:text-xs font-medium ${colors.text}`}>
                        {label}
                      </span>
                      {i < arr.length - 1 && (
                        <span className="text-gray-300 dark:text-gray-700 text-[10px]">·</span>
                      )}
                    </span>
                  )
                })}
              </div>
            )}

            {/* Actions - larger touch targets on mobile */}
            {!isUser && (
              <div className="mt-2 sm:mt-3 flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="p-2 sm:p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay rounded transition-colors touch-manipulation"
                  title="Copy to clipboard"
                >
                  {copied ? <Check size={16} className="sm:w-3.5 sm:h-3.5" /> : <Copy size={16} className="sm:w-3.5 sm:h-3.5" />}
                </button>
                <button
                  onClick={() => handleFeedback('up')}
                  disabled={!!feedback}
                  className={`p-2 sm:p-1.5 rounded transition-colors touch-manipulation ${
                    feedback === 'up'
                      ? 'text-green-500'
                      : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay'
                  } ${feedback && feedback !== 'up' ? 'opacity-40 cursor-default' : ''}`}
                  title="Helpful"
                >
                  <ThumbsUp size={14} fill={feedback === 'up' ? 'currentColor' : 'none'} />
                </button>
                <button
                  onClick={() => handleFeedback('down')}
                  disabled={!!feedback}
                  className={`p-2 sm:p-1.5 rounded transition-colors touch-manipulation ${
                    feedback === 'down'
                      ? 'text-red-500'
                      : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay'
                  } ${feedback && feedback !== 'down' ? 'opacity-40 cursor-default' : ''}`}
                  title="Not helpful"
                >
                  <ThumbsDown size={14} fill={feedback === 'down' ? 'currentColor' : 'none'} />
                </button>
              </div>
            )}

            {/* Timing display for assistant messages */}
            {!isUser && timing && (
              <TimingDisplay timing={timing} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
})

/**
 * Chat Input Component
 */
function ChatInput({ onSend, isLoading, onCancel, maxWidthClass = 'max-w-3xl' }) {
  const [input, setInput] = useState('')
  const [attachments, setAttachments] = useState([]) // [{file, preview, base64, type}]
  const [isProcessingFiles, setIsProcessingFiles] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const textareaRef = useRef(null)
  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)
  const dragCounter = useRef(0) // Track drag enter/leave for nested elements

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px'
    }
  }, [input])

  const handleSubmit = () => {
    if ((!input.trim() && attachments.length === 0) || isLoading) return
    onSend(input.trim(), attachments)
    setInput('')
    setAttachments([])
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleFileClick = () => {
    fileInputRef.current?.click()
  }

  // Shared function to process files (used by both click upload and drag-drop)
  const processFiles = async (files) => {
    if (files.length === 0) return

    // Filter to only allowed types
    const allowedTypes = [
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp',
      'application/pdf',
      'text/csv', 'text/plain', 'text/tab-separated-values',
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
    ]
    // Structured files the backend parses generically (text + spreadsheets).
    // Match by extension too, since MIME types vary across OSes (e.g. .txt may
    // report as application/octet-stream).
    const structuredExts = ['.csv', '.tsv', '.dat', '.tab', '.txt', '.xls', '.xlsx', '.xlsm']
    const validFiles = files.filter(file =>
      allowedTypes.includes(file.type) ||
      file.type.startsWith('text/') ||
      structuredExts.some(ext => file.name.toLowerCase().endsWith(ext))
    )

    if (validFiles.length === 0) return

    setIsProcessingFiles(true)

    try {
      const newAttachments = await Promise.all(
        validFiles.map(async (file) => {
          // Read file as base64
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result)
            reader.readAsDataURL(file)
          })

          // Determine type
          const isImage = file.type.startsWith('image/')
          const isPDF = file.type === 'application/pdf'
          const isCSV = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
          const isExcel = file.type === 'application/vnd.ms-excel' ||
                          file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                          file.name.toLowerCase().endsWith('.xls') ||
                          file.name.toLowerCase().endsWith('.xlsx') ||
                          file.name.toLowerCase().endsWith('.xlsm')

          return {
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            isImage,
            isPDF,
            isCSV,
            isExcel,
            base64,
            preview: isImage ? base64 : null
          }
        })
      )

      setAttachments(prev => [...prev, ...newAttachments])
    } finally {
      setIsProcessingFiles(false)
    }
  }

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || [])
    await processFiles(files)
    // Reset file input
    e.target.value = ''
  }

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounter.current = 0

    if (isLoading) return

    const files = Array.from(e.dataTransfer.files || [])
    await processFiles(files)
  }

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  return (
    <div
      ref={dropZoneRef}
      className={`border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-surface-dark p-2 sm:p-4 safe-area-bottom relative transition-colors ${
        isDragging ? 'bg-blue-50 dark:bg-blue-950/30' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop overlay */}
      {isDragging && (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-50/90 dark:bg-blue-950/80 border-2 border-dashed border-amtrend-blue rounded-lg z-10 pointer-events-none">
          <div className="text-center">
            <Paperclip size={32} className="mx-auto text-amtrend-blue mb-2" />
            <p className="text-amtrend-blue font-medium">Drop files here</p>
            <p className="text-xs text-blue-400 mt-1">Images, PDFs, Excel, CSV</p>
          </div>
        </div>
      )}

      <div className={`${maxWidthClass} mx-auto`}>
        {/* Attachment Previews - smaller on mobile */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 sm:mb-3">
            {attachments.map((attachment, index) => (
              <div
                key={index}
                className="relative group bg-gray-100 dark:bg-surface-dark-overlay rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
              >
                {attachment.isImage ? (
                  <img
                    src={attachment.preview}
                    alt={attachment.name}
                    className="h-16 w-16 sm:h-20 sm:w-20 object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 sm:h-20 sm:w-20 flex flex-col items-center justify-center p-2">
                    <span className="text-xl sm:text-2xl">
                      {attachment.isPDF ? '📄' : attachment.isExcel ? '📊' : attachment.isCSV ? '📋' : '📎'}
                    </span>
                    <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate w-full text-center mt-1">
                      {attachment.name.length > 10
                        ? attachment.name.slice(0, 8) + '...'
                        : attachment.name}
                    </span>
                  </div>
                )}
                {/* Always visible delete button on mobile for easier tapping */}
                <button
                  onClick={() => removeAttachment(index)}
                  className="absolute -top-1 -right-1 w-6 h-6 sm:w-5 sm:h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity touch-manipulation"
                >
                  ×
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] sm:text-[10px] px-1 py-0.5 truncate">
                  {formatFileSize(attachment.size)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="relative flex items-end gap-1.5 sm:gap-2 bg-gray-50 dark:bg-surface-dark-raised border border-gray-200 dark:border-gray-700 rounded-2xl px-2 sm:px-4 py-2 sm:py-3 focus-within:border-amtrend-blue focus-within:ring-2 focus-within:ring-amtrend-blue/20 transition-all">
          {/* File Upload Button - larger touch target on mobile */}
          <button
            onClick={handleFileClick}
            disabled={isProcessingFiles || isLoading}
            className={`p-2 sm:p-1.5 rounded-lg transition-colors touch-manipulation ${
              isProcessingFiles
                ? 'text-amtrend-blue animate-pulse'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-surface-dark-overlay active:bg-gray-300 dark:active:bg-gray-700'
            }`}
            title={isProcessingFiles ? "Processing file..." : "Attach image or PDF"}
          >
            {isProcessingFiles ? (
              <div className="w-5 h-5 border-2 border-amtrend-blue border-t-transparent rounded-full animate-spin" />
            ) : (
              <Paperclip size={22} className="sm:w-5 sm:h-5" />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,application/pdf,text/csv,.csv,text/plain,.txt,.tsv,.dat,.tab,application/vnd.ms-excel,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,.xlsm"
            onChange={handleFileChange}
            multiple
          />

          {/* Text Input - better font size for mobile to prevent zoom */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={attachments.length > 0
              ? "Add a message..."
              : "Ask about orders, quotes, jobs..."}
            className="flex-1 bg-transparent border-0 resize-none focus:outline-none focus:ring-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-base sm:text-sm max-h-[120px] sm:max-h-[200px]"
            rows={1}
            disabled={isLoading}
            style={{ fontSize: '16px' }} // Prevents zoom on iOS
          />

          {/* Send/Stop Button - larger touch target */}
          {isLoading ? (
            <button
              onClick={onCancel}
              className="p-2.5 sm:p-2 bg-gray-200 dark:bg-surface-dark-overlay text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700 active:bg-gray-400 dark:active:bg-gray-600 transition-colors touch-manipulation"
              title="Stop generating"
            >
              <StopCircle size={22} className="sm:w-5 sm:h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!input.trim() && attachments.length === 0}
              className="p-2.5 sm:p-2 bg-amtrend-blue text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed transition-colors touch-manipulation"
              title="Send message"
            >
              <Send size={22} className="sm:w-5 sm:h-5" />
            </button>
          )}
        </div>

        <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 text-center mt-1.5 sm:mt-2">
          Trendy uses Epicor data. Always verify critical decisions.
        </p>
      </div>
    </div>
  )
}

/**
 * Loading/Streaming Indicator — uses 3-phase ProgressIndicator
 * when no content is streaming yet, falls back to streaming content
 * display once text starts arriving.
 */
/**
 * During streaming, structured blocks (tables, KPI cards, charts) arrive as
 * JSON inside ``` code fences and only render as components once the closing
 * fence streams in. Showing the half-streamed JSON looks like "watching it
 * render." This hides an in-progress (unterminated) fenced block and leaves a
 * subtle placeholder, so prose streams smoothly and structured blocks appear
 * already-rendered. Once the closing ``` arrives the fence count is even and
 * the real content shows.
 */
function smoothStreamingContent(text) {
  if (!text) return text

  // 1. In-progress (unterminated) code fence → hide it (covers JSON-based
  //    KPI cards / charts / inline visuals that render on a closed fence).
  const fenceCount = (text.match(/```/g) || []).length
  if (fenceCount % 2 === 1) {
    const lastFence = text.lastIndexOf('```')
    const head = text.slice(0, lastFence).replace(/\s+$/, '')
    return head ? `${head}\n\n*Preparing…*` : '*Preparing…*'
  }

  // 2. In-progress markdown table → hide it until complete. A streaming table
  //    re-renders row-by-row (janky); replace the trailing run of table rows
  //    (>=2 consecutive lines starting with "|") with a placeholder. It reveals
  //    fully-formed once the model writes past the table, or at end-of-stream
  //    when the final message renders the complete table.
  const lines = text.split('\n')
  let end = lines.length
  while (end > 0 && lines[end - 1].trim() === '') end--          // skip trailing blanks
  let start = end
  while (start - 1 >= 0 && lines[start - 1].trim().startsWith('|')) start--
  if (end - start >= 2) {
    const head = lines.slice(0, start).join('\n').replace(/\s+$/, '')
    return head ? `${head}\n\n*Preparing table…*` : '*Preparing table…*'
  }

  return text
}

function StreamingMessage({ content, activeTools, hasAttachments, maxWidthClass = 'max-w-3xl' }) {
  return (
    <div className="py-4 sm:py-6 bg-gray-50 dark:bg-surface-dark-raised">
      <div className={`${maxWidthClass} mx-auto px-3 sm:px-4`}>
        <div className="flex gap-2 sm:gap-4">
          <div className="flex-shrink-0">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-amtrend-blue rounded-lg flex items-center justify-center">
              <span className="text-white text-xs sm:text-sm font-semibold">T</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">Trendy</span>
            </div>

            {/* Attachment processing indicator */}
            {hasAttachments && !content && activeTools.length === 0 && (
              <div className="mb-2 sm:mb-3 flex items-center gap-2">
                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-[10px] sm:text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mr-1.5 sm:mr-2 animate-pulse" />
                  Analyzing attachment...
                </span>
              </div>
            )}

            {/* Streaming content with live cursor — incomplete structured
                blocks are hidden until their closing fence arrives so output
                streams smoothly instead of showing raw JSON building up. */}
            {content && (
              <div className="prose prose-sm max-w-none text-gray-500 dark:text-gray-400 italic text-sm sm:text-base">
                <MessageContent content={smoothStreamingContent(content)} />
                <span className="inline-block w-1 h-4 bg-amtrend-blue ml-0.5 animate-pulse" />
              </div>
            )}
            {/* In-process indicator: show while waiting for the first text, AND
                whenever a tool or web search is still running mid-response — so
                a tool/search pause after some text never looks frozen. */}
            {(!content || activeTools.some(t => t.status === 'running' || t.status === 'awaiting_approval')) && (
              <div className={content ? 'mt-2 sm:mt-3' : ''}>
                <ProgressIndicator activeTools={activeTools} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Keyboard Shortcuts Popover
 */
function ShortcutsPopover({ onClose }) {
  const ref = useRef(null)

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  const isMac = navigator.platform?.toUpperCase().includes('MAC')
  const mod = isMac ? '⌘' : 'Ctrl'

  const shortcuts = [
    { keys: `${mod} K`, label: 'Search conversations' },
    { keys: `${mod} N`, label: 'New conversation' },
    { keys: `${mod} B`, label: 'Toggle sidebar' },
    { keys: `${mod} ⇧ D`, label: 'Toggle dark mode' },
    { keys: 'Esc', label: 'Close panel' },
  ]

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-64 z-50 bg-white dark:bg-surface-dark-raised rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 animate-fade-in"
      style={{ animationDuration: '150ms' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Keyboard Shortcuts</span>
        <button onClick={onClose} className="p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-2">
        {shortcuts.map(({ keys, label }) => (
          <div key={keys} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-dark-overlay transition-colors">
            <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
            <kbd className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-overlay border border-gray-200 dark:border-gray-700 rounded-md">
              {keys}
            </kbd>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * M365 Reconnect Banner — shown when M365 token is missing/expired after a deploy.
 * Self-contained: checks status on mount, dismissible per session.
 */
function M365ReconnectBanner() {
  const [show, setShow] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [reason, setReason] = useState('disconnected')

  useEffect(() => {
    // Don't nag if user already dismissed this session
    if (sessionStorage.getItem('m365_banner_dismissed')) return

    const check = async () => {
      try {
        const token = localStorage.getItem('amtrend_token')
        if (!token) { console.log('[M365Banner] No token'); return }
        const res = await fetch('/api/auth/m365/status', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) { console.log('[M365Banner] Status endpoint returned', res.status); return }
        const data = await res.json()
        console.log('[M365Banner] Status:', data)
        // Show banner if M365 is configured but user is not connected (or token expired)
        // Also show if configured is not explicitly returned (backwards compat)
        const isConfigured = data.configured !== false
        // can_draft === false means the token predates Mail.ReadWrite. The
        // connection works for reading, so it needs prompting without being
        // described as broken — hence the separate reason, and separate copy.
        const needsScopes = data.connected && data.can_draft === false
        if (isConfigured && (!data.connected || data.is_expired || needsScopes)) {
          console.log('[M365Banner] Showing banner', { needsScopes })
          setReason(needsScopes ? 'scopes' : 'disconnected')
          setShow(true)
        }
      } catch (err) { console.log('[M365Banner] Error:', err) }
    }
    check()
  }, [])

  const handleConnect = async () => {
    setConnecting(true)
    try {
      const token = localStorage.getItem('amtrend_token')
      const res = await fetch('/api/auth/m365/login', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        window.location.href = data.auth_url
      }
    } catch {
      setConnecting(false)
    }
  }

  const handleDismiss = () => {
    setShow(false)
    sessionStorage.setItem('m365_banner_dismissed', 'true')
  }

  if (!show) return null

  return (
    <div className="mx-auto max-w-3xl px-4 pt-3">
      <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <svg viewBox="0 0 23 23" className="w-5 h-5 flex-shrink-0" fill="currentColor">
          <path fill="#f25022" d="M1 1h10v10H1z"/>
          <path fill="#00a4ef" d="M1 12h10v10H1z"/>
          <path fill="#7fba00" d="M12 1h10v10H12z"/>
          <path fill="#ffb900" d="M12 12h10v10H12z"/>
        </svg>
        <p className="flex-1 text-sm text-blue-800 dark:text-blue-200">
          {reason === 'scopes'
            ? 'Reconnect Microsoft 365 to let Trendy prepare draft replies in your Outlook. Email, calendar and SharePoint keep working either way.'
            : 'Your Microsoft 365 session needs to be reconnected to use email, calendar, and SharePoint.'}
        </p>
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {connecting ? 'Connecting...' : 'Reconnect'}
        </button>
        <button
          onClick={handleDismiss}
          className="p-1 text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300 transition-colors flex-shrink-0"
          title="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  )
}

/**
 * Main ChatApp Component
 */
function ChatAppInner() {
  // PR4: stash the right-pane action setter so the chatStream callback can
  // route enriched approval events to the ActionCardView in the right pane.
  const { setPendingAction } = useSourcePane()
  const {
    currentSession,
    currentSessionId,
    createSession,
    selectSession,
    addMessage,
    updateSessionMessages,
  } = useSessions()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { theme, isDark, setTheme } = useTheme()
  const [isLoading, setIsLoading] = useState(false)
  // Start sidebar closed on mobile (< lg breakpoint), open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('trendy_sidebar_collapsed') === 'true' } catch { return false }
  })
  const [streamingContent, setStreamingContent] = useState('')
  const [activeTools, setActiveTools] = useState([]) // [{ name, status: 'running' | 'done' | 'error' }]
  const [hasAttachments, setHasAttachments] = useState(false)
  const [pendingApproval, setPendingApproval] = useState(null) // { approvalId, toolName, toolInput }

  // ── rAF batching refs for smooth streaming ──
  const streamBufferRef = useRef('')
  const rafIdRef = useRef(null)
  const [artifacts, setArtifacts] = useState([])  // Array of artifacts for this session
  const [activeArtifactIndex, setActiveArtifactIndex] = useState(0)  // Currently active artifact tab
  const [artifactMinimized, setArtifactMinimized] = useState(false)  // Minimize/maximize state
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)

  // Chat width mode — "wide" gives tables room to breathe
  const CHAT_WIDTH_KEY = 'trendy_chat_wide'
  const [chatWideMode, setChatWideMode] = useState(() => {
    try { return localStorage.getItem(CHAT_WIDTH_KEY) === 'true' } catch { return false }
  })
  const maxWidthClass = chatWideMode ? 'max-w-6xl' : 'max-w-3xl'
  const toggleChatWidth = () => {
    setChatWideMode(prev => {
      const next = !prev
      try { localStorage.setItem(CHAT_WIDTH_KEY, String(next)) } catch {}
      return next
    })
  }

  const messagesEndRef = useRef(null)
  // Scroll container for the message list. We watch it to decide whether the
  // user is "pinned" to the bottom; auto-follow only happens when they are, so
  // scrolling up to re-read is never interrupted by the incoming stream.
  const scrollContainerRef = useRef(null)
  const pinnedRef = useRef(true)
  // Shows the "jump to latest" pill when the user has scrolled up away from the
  // bottom (so following is paused and they have a one-tap way back down).
  const [showScrollButton, setShowScrollButton] = useState(false)
  // Cadence: the in-progress rAF reveal loop. Drives how fast received text is
  // shown (see startCadence). Held in a ref so we can cancel it on cleanup.
  const cadenceRef = useRef(null)

  // ── Per-session stream map for concurrent streaming ──
  // Each running stream gets an entry: { controller, content, tools, toolsUsed }
  // The UI only shows the active session's stream. Background streams keep running.
  const streamsRef = useRef(new Map())
  // Mirror currentSessionId in a ref so stream closures can read it
  const activeSessionIdRef = useRef(currentSessionId)
  activeSessionIdRef.current = currentSessionId

  // Get messages from current session
  const messages = currentSession?.messages || []

  // Track previous session ID to detect switches
  const prevSessionIdRef = useRef(currentSessionId)

  // Sync UI state when session changes — DON'T abort background streams
  useEffect(() => {
    if (prevSessionIdRef.current !== currentSessionId) {
      prevSessionIdRef.current = currentSessionId

      // Cancel pending rAF from the old session
      if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null }
      if (cadenceRef.current) { cancelAnimationFrame(cadenceRef.current); cadenceRef.current = null }
      streamBufferRef.current = ''

      // Check if the new session has an active stream — resume showing it
      const existingStream = currentSessionId ? streamsRef.current.get(currentSessionId) : null
      if (existingStream) {
        // Show what's already there at once (no replaying the typewriter); any
        // further deltas resume the cadence from this point.
        existingStream.shown = existingStream.content.length
        setStreamingContent(existingStream.content)
        setActiveTools([...existingStream.tools])
        setIsLoading(true)
        setHasAttachments(false)
      } else {
        // No active stream — reset UI
        setStreamingContent('')
        setActiveTools([])
        setIsLoading(false)
        setHasAttachments(false)
      }

      // Restore artifacts array from localStorage for this session
      if (currentSessionId) {
        const { artifacts: savedArtifacts, activeIndex } = getArtifactsForSession(currentSessionId)
        if (savedArtifacts.length > 0) {
          console.log('Restoring artifacts for session:', currentSessionId, savedArtifacts.length, 'artifacts')
          setArtifacts(savedArtifacts)
          setActiveArtifactIndex(Math.min(activeIndex, savedArtifacts.length - 1))
          setArtifactMinimized(false)
        } else {
          setArtifacts([])
          setActiveArtifactIndex(0)
          setArtifactMinimized(false)
        }
      } else {
        setArtifacts([])
        setActiveArtifactIndex(0)
        setArtifactMinimized(false)
      }
    }
  }, [currentSessionId])

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const mod = e.metaKey || e.ctrlKey

      // Cmd/Ctrl+N — New conversation (background streams keep running)
      if (mod && e.key === 'n') {
        e.preventDefault()
        handleHeaderNewChat()
      }

      // Cmd/Ctrl+B — Toggle sidebar
      if (mod && e.key === 'b') {
        e.preventDefault()
        setSidebarCollapsed(prev => {
          const next = !prev
          try { localStorage.setItem('trendy_sidebar_collapsed', String(next)) } catch {}
          return next
        })
      }

      // Cmd/Ctrl+K — Open search modal
      if (mod && e.key === 'k') {
        e.preventDefault()
        setSearchModalOpen(prev => !prev)
      }

      // Cmd/Ctrl+Shift+D — Toggle dark mode
      if (mod && e.shiftKey && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault()
        setTheme(theme === 'system' ? 'dark' : theme === 'dark' ? 'light' : 'system')
      }

      // Escape — Close search modal first, then artifact panel
      if (e.key === 'Escape') {
        if (searchModalOpen) {
          setSearchModalOpen(false)
        } else if (artifacts.length > 0) {
          setArtifacts([])
          setActiveArtifactIndex(0)
          setArtifactMinimized(false)
          if (currentSessionId) clearArtifactsForSession(currentSessionId)
        }
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isLoading, createSession, artifacts.length, currentSessionId, searchModalOpen, theme, setTheme])

  // Track whether the user is pinned to the bottom of the message list. A
  // small threshold (120px) counts "close enough" as pinned so a tiny manual
  // nudge doesn't disable follow. When they scroll up, surface the pill.
  const handleMessagesScroll = () => {
    const el = scrollContainerRef.current
    if (!el) return
    const pinned = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    pinnedRef.current = pinned
    setShowScrollButton(prev => (prev === !pinned ? prev : !pinned))
  }

  const scrollToBottom = () => {
    const el = scrollContainerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    pinnedRef.current = true
    setShowScrollButton(false)
  }

  // Follow the stream — but ONLY if the user is pinned to the bottom, and by
  // jumping scrollTop directly (instant). Animated scrollIntoView on each token
  // restarts a smooth-scroll every frame and fights the user's own scroll —
  // that tug is the "yank". Instant glue keeps the latest line in view without
  // animating, exactly like a terminal tailing output.
  useEffect(() => {
    if (!pinnedRef.current) return
    const el = scrollContainerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, isLoading, streamingContent])

  // Cancel any in-flight cadence reveal loop on unmount.
  useEffect(() => () => {
    if (cadenceRef.current) cancelAnimationFrame(cadenceRef.current)
  }, [])

  // Send message handler with concurrent streaming support.
  // Each session gets its own stream entry in streamsRef. Background streams
  // keep running silently when you switch to another conversation.
  const handleSendMessage = useCallback(async (content, attachments = [], explicitSessionId = null) => {
    // Create session if none exists (use explicit ID if caller passed one — avoids race)
    let sessionId = explicitSessionId || currentSessionId
    if (!sessionId) {
      sessionId = await createSession()
      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 50))
    }

    // Build user message content - include attachment info for display
    let displayContent = content
    if (attachments.length > 0) {
      const attachmentNames = attachments.map(a => a.name).join(', ')
      if (!displayContent) {
        displayContent = `[Attached: ${attachmentNames}]`
      }
    }

    // Add user message with attachments for display
    const userMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: displayContent,
      attachments: attachments.map(a => ({
        name: a.name,
        type: a.type,
        isImage: a.isImage,
        isPDF: a.isPDF,
        preview: a.preview,
      })),
      timestamp: new Date().toISOString(),
    }
    addMessage(userMessage, sessionId)

    // If this session already has an active stream, abort it first
    const existingStream = streamsRef.current.get(sessionId)
    if (existingStream) {
      existingStream.controller.abort()
      streamsRef.current.delete(sessionId)
    }

    // Create per-session stream entry
    const controller = new AbortController()
    // citations: per-message LRU-ordered Citation[] collected from tool_result events.
    // citationIds: deduplication set so re-emits of the same id don't pile up.
    // shown: chars currently revealed to the UI (cadence); done: stream ended.
    const streamState = { controller, content: '', shown: 0, done: false, tools: [], toolsUsed: [], citations: [], citationIds: new Set(), groundingAlert: null }
    streamsRef.current.set(sessionId, streamState)

    setIsLoading(true)
    setStreamingContent('')
    setActiveTools([])
    setHasAttachments(attachments.length > 0)
    streamBufferRef.current = ''
    pinnedRef.current = true // a fresh send should follow the new response
    if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null }
    if (cadenceRef.current) { cancelAnimationFrame(cadenceRef.current); cadenceRef.current = null }

    // Build history from current messages
    const history = messages.slice(-10).map(msg => ({
      role: msg.role,
      content: msg.content,
    }))

    // Track timing for the final message
    let responseTiming = null

    // rAF flush for TOOL/loading state only. Content is revealed separately by
    // the cadence loop (startCadence) so text flows at a steady pace instead of
    // lurching with each network burst.
    const scheduleFlush = () => {
      if (sessionId !== activeSessionIdRef.current) return // background — skip UI
      if (rafIdRef.current) return // already scheduled
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null
        const stream = streamsRef.current.get(sessionId)
        if (stream && sessionId === activeSessionIdRef.current) {
          setActiveTools([...stream.tools])
          setIsLoading(true)
        }
      })
    }

    // Cadence reveal. `stream.content` is everything RECEIVED; `stream.shown` is
    // how much is currently DISPLAYED. Each frame we advance `shown` toward the
    // total by a fraction of the gap (with a small floor), so bursts of network
    // text get spread into a smooth, readable reveal. The loop self-stops when
    // it catches up; a new delta restarts it. When the stream is done we reveal
    // the remainder immediately so the finish never drags.
    const startCadence = () => {
      if (cadenceRef.current) return // already running
      if (sessionId !== activeSessionIdRef.current) return // background — skip UI
      const step = () => {
        cadenceRef.current = null
        const stream = streamsRef.current.get(sessionId)
        if (!stream || sessionId !== activeSessionIdRef.current) return
        const total = stream.content.length
        if (stream.shown < total) {
          const gap = total - stream.shown
          const advance = stream.done ? gap : Math.max(3, Math.ceil(gap * 0.18))
          stream.shown = Math.min(total, stream.shown + advance)
          setStreamingContent(stream.content.slice(0, stream.shown))
        }
        // Keep going while there's more to reveal; otherwise stop and let the
        // next delta (or completion) restart us.
        if (stream.shown < total) {
          cadenceRef.current = requestAnimationFrame(step)
        }
      }
      cadenceRef.current = requestAnimationFrame(step)
    }

    try {
      // Log for debugging
      console.log('Sending message with attachments:', {
        content,
        attachmentCount: attachments.length,
        attachmentTypes: attachments.map(a => ({ name: a.name, type: a.type, isImage: a.isImage }))
      })

      // Try streaming first — pass abort signal for real cancellation
      await chatStream(
        content,
        history,
        // onDelta - write to per-session stream state, reveal via cadence
        (delta) => {
          streamState.content += delta
          startCadence()
        },
        // onToolCall - track in stream state
        (tool) => {
          if (!streamState.tools.some(t => t.name === tool)) {
            streamState.tools = [...streamState.tools, { name: tool, status: 'running' }]
          }
          if (!streamState.toolsUsed.includes(tool)) {
            streamState.toolsUsed.push(tool)
          }
          scheduleFlush()
        },
        // onDone - merge final tool list, capture timing
        (toolsUsed, timing) => {
          if (toolsUsed && toolsUsed.length > 0) {
            streamState.toolsUsed = [...new Set([...streamState.toolsUsed, ...toolsUsed])]
          }
          streamState.tools = streamState.tools.map(t => ({ ...t, status: 'done' }))
          if (timing) responseTiming = timing
          // Flush so the final all-done status renders (every sibling callback
          // flushes; without this the 'done' transition could be dropped).
          scheduleFlush()
          // Completion must reveal everything RECEIVED immediately and WITHOUT
          // depending on the cadence rAF — rAF pauses in background tabs and is
          // cancelled at commit, either of which could otherwise leave the tail
          // unshown ("stream didn't finish"). Snap shown to full and push it.
          streamState.done = true
          streamState.shown = streamState.content.length
          if (cadenceRef.current) { cancelAnimationFrame(cadenceRef.current); cadenceRef.current = null }
          if (sessionId === activeSessionIdRef.current) {
            setStreamingContent(streamState.content)
          }
        },
        // attachments
        attachments,
        // onArtifact — only update UI if active, otherwise persist silently
        (artifactData) => {
          console.log('Artifact received:', artifactData)
          if (sessionId === activeSessionIdRef.current) {
            setArtifacts(prev => {
              const newArtifacts = [...prev, artifactData]
              const newIndex = newArtifacts.length - 1
              setActiveArtifactIndex(newIndex)
              if (sessionId && artifactData) {
                saveArtifactsForSession(sessionId, newArtifacts, newIndex)
              }
              return newArtifacts
            })
            setArtifactMinimized(false)
          } else if (sessionId && artifactData) {
            const { artifacts: existing } = getArtifactsForSession(sessionId)
            const updated = [...existing, artifactData]
            saveArtifactsForSession(sessionId, updated, updated.length - 1)
          }
        },
        // onToolResult - update stream state
        (tool, success) => {
          streamState.tools = streamState.tools.map(t =>
            t.name === tool ? { ...t, status: success ? 'done' : 'error' } : t
          )
          scheduleFlush()
        },
        // signal — wire abort to the actual fetch
        controller.signal,
        // onApprovalRequired — write tool needs user confirmation
        (approvalId, toolName, toolInput, rawActionCard, expiresAt) => {
          // Add tool to active tools with 'awaiting_approval' status
          if (!streamState.tools.some(t => t.name === toolName)) {
            streamState.tools = [...streamState.tools, { name: toolName, status: 'awaiting_approval' }]
          } else {
            streamState.tools = streamState.tools.map(t =>
              t.name === toolName ? { ...t, status: 'awaiting_approval' } : t
            )
          }
          scheduleFlush()

          // PR4: prefer the ActionCard right-pane flow when the SSE
          // payload carried action_card. zod-validates before pushing into
          // the source pane; on parse failure, fall back to the legacy
          // modal so the user still has a way to approve.
          if (rawActionCard) {
            const validated = parseActionCardSafe(rawActionCard)
            if (validated) {
              setPendingAction(validated, approvalId, expiresAt)
              return
            }
            // eslint-disable-next-line no-console
            console.warn('ActionCard failed validation; falling back to legacy modal')
          }
          // Legacy prose-approval modal
          setPendingApproval({ approvalId, toolName, toolInput })
        },
        // onCitations — tool returned citable sources (PR1)
        (_toolName, rawCitations) => {
          const validated = parseCitationsSafe(rawCitations)
          if (validated.length === 0) return
          for (const c of validated) {
            if (streamState.citationIds.has(c.id)) continue
            streamState.citationIds.add(c.id)
            streamState.citations.push(c)
          }
        },
        // conversationId — required by backend _persisting_stream_wrapper to
        // INSERT the assistant message at end-of-stream. Without this, the
        // server-side persistence guard short-circuits and the response is lost.
        sessionId,
        // onGroundingAlert — backend flagged unverified/fabricated values or
        // hand-built links just before 'done'. Stash the alert for the message
        // banner AND neutralize the bad deep links in the streamed content live.
        (violations, badUrls) => {
          streamState.groundingAlert = { violations: violations || [], badUrls: badUrls || [] }
          // Neutralize hand-built deep links LIVE: drop the link target, keep the label.
          if (Array.isArray(badUrls)) {
            for (const u of badUrls) {
              const esc = u.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
              streamState.content = streamState.content
                .replace(new RegExp('\\[([^\\]]+)\\]\\(' + esc + '\\)', 'g'), '$1 (⚠️ unverified link removed)')
                .split(u).join('⚠️ unverified link removed')
            }
          }
          if (sessionId === activeSessionIdRef.current) setStreamingContent(streamState.content)
        }
      )

      // Stream completed — clean up and persist
      streamsRef.current.delete(sessionId)

      // Extract panel artifacts (chart, relationship_graph, workflow_diagram, what_if_calculator)
      // from JSON code blocks → strip from chat content → push to artifact panel.
      // Inline visuals (type:"inline") are NOT extracted — they stay in the message and
      // MessageContent.jsx renders them via the <pre> interceptor in markdownComponents.
      let cleanedContent = streamState.content
      const PANEL_ARTIFACT_TYPES = new Set(['chart', 'relationship_graph', 'workflow_diagram', 'what_if_calculator', 'email_compose'])
      try {
        // Match ALL code blocks (with or without json tag), then check if they're panel artifacts
        const codeBlockRegex = /```(?:json)?\s*\n([\s\S]*?)\n\s*```/g
        let match
        const extractedArtifacts = []
        const blocksToStrip = []

        while ((match = codeBlockRegex.exec(streamState.content)) !== null) {
          try {
            const parsed = JSON.parse(match[1].trim())
            if (parsed && PANEL_ARTIFACT_TYPES.has(parsed.type)) {
              extractedArtifacts.push(parsed)
              blocksToStrip.push(match[0]) // Store the full match for stripping
            }
          } catch (parseErr) {
            // Not valid JSON or not a panel artifact — leave it alone
          }
        }

        // Strip successfully extracted blocks from the chat content
        if (extractedArtifacts.length > 0) {
          for (const block of blocksToStrip) {
            cleanedContent = cleanedContent.replace(block, '')
          }
          cleanedContent = cleanedContent.replace(/\n{3,}/g, '\n\n').trim()

          // Push to artifact panel
          for (const artifactData of extractedArtifacts) {
            if (sessionId === activeSessionIdRef.current) {
              setArtifacts(prev => {
                const updated = [...prev, artifactData]
                setActiveArtifactIndex(updated.length - 1)
                saveArtifactsForSession(sessionId, updated, updated.length - 1)
                return updated
              })
              setArtifactMinimized(false)
            }
          }
        }
      } catch (extractErr) {
        // Never let artifact extraction break the chat
      }

      const assistantMessage = {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: cleanedContent,
        toolsUsed: streamState.toolsUsed,
        citations: streamState.citations.length > 0 ? streamState.citations : undefined,
        groundingAlert: streamState.groundingAlert || undefined,
        timing: responseTiming,
        timestamp: new Date().toISOString(),
      }
      // Server-side persistence (chat.py:_persisting_stream_wrapper) already
      // INSERTed this message at end-of-stream — don't double-save.
      addMessage(assistantMessage, sessionId, { skipServerPersist: true })

      // Only reset UI if this is still the active session
      if (sessionId === activeSessionIdRef.current) {
        if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null }
        if (cadenceRef.current) { cancelAnimationFrame(cadenceRef.current); cadenceRef.current = null }
        setIsLoading(false)
        setStreamingContent('')
        streamBufferRef.current = ''
        setActiveTools([])
        setHasAttachments(false)
      }

    } catch (err) {
      // Clean up stream entry
      streamsRef.current.delete(sessionId)

      const resetUIIfActive = () => {
        if (sessionId === activeSessionIdRef.current) {
          if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = null }
          if (cadenceRef.current) { cancelAnimationFrame(cadenceRef.current); cadenceRef.current = null }
          setIsLoading(false)
          setStreamingContent('')
          streamBufferRef.current = ''
          setActiveTools([])
          setHasAttachments(false)
        }
      }

      // User cancelled (stop button) — save partial if substantial
      if (err.name === 'AbortError') {
        if (streamState.content && streamState.content.length > 100) {
          const partialMessage = {
            id: `msg_${Date.now()}`,
            role: 'assistant',
            content: streamState.content,
            toolsUsed: streamState.toolsUsed.length > 0 ? streamState.toolsUsed : undefined,
            citations: streamState.citations.length > 0 ? streamState.citations : undefined,
            groundingAlert: streamState.groundingAlert || undefined,
            timing: responseTiming,
            timestamp: new Date().toISOString(),
          }
          // Server's finally-block INSERT already captured whatever tokens arrived
          // before the abort — skip the client POST to avoid duplicates.
          addMessage(partialMessage, sessionId, { skipServerPersist: true })
        }
        resetUIIfActive()
        return
      }

      // If streaming fails, try fallback to non-streaming
      if (streamState.content === '') {
        console.warn('Streaming failed, falling back to non-streaming:', err.message)

        try {
          const response = await chat(content, history)
          const assistantMessage = {
            id: `msg_${Date.now()}`,
            role: 'assistant',
            content: response.response || response.message,
            toolsUsed: response.tools_used || [],
            timing: response.timing || null,
            timestamp: new Date().toISOString(),
          }
          addMessage(assistantMessage, sessionId)
          resetUIIfActive()
          return
        } catch (fallbackErr) {
          const errorMessage = {
            id: `msg_${Date.now()}`,
            role: 'assistant',
            content: `I encountered an error: ${fallbackErr.message || 'Unknown error'}. Please try again.`,
            timestamp: new Date().toISOString(),
          }
          addMessage(errorMessage, sessionId)
          resetUIIfActive()
          return
        }
      }

      // Streaming started but failed mid-way - save what we have
      if (streamState.content) {
        const trimmed = streamState.content.trim()
        const looksComplete = /[.!?:]$/.test(trimmed) ||
                              trimmed.endsWith('```') ||
                              trimmed.endsWith('|') ||
                              trimmed.length > 500

        const partialMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: looksComplete ? streamState.content : streamState.content + '\n\n*[Response may be incomplete]*',
          toolsUsed: streamState.toolsUsed,
          timing: responseTiming,
          timestamp: new Date().toISOString(),
        }
        // Server's finally-block INSERT captured the partial tokens already —
        // skip the client POST. Note: the server saves the raw content without
        // the "[Response may be incomplete]" annotation. Acceptable trade-off
        // for Phase 1; can be revisited if the annotation is important.
        addMessage(partialMessage, sessionId, { skipServerPersist: true })
      } else {
        const errorMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: `I encountered an error: ${err.message || 'Unknown error'}. Please try again.`,
          timestamp: new Date().toISOString(),
        }
        addMessage(errorMessage, sessionId)
      }

      resetUIIfActive()
    }
  }, [currentSessionId, messages, createSession, addMessage])

  // Cancel request handler — aborts only the ACTIVE session's stream
  const handleCancel = () => {
    const convId = activeSessionIdRef.current
    if (!convId) return
    const stream = streamsRef.current.get(convId)
    if (stream) {
      stream.controller.abort()
      // Stream cleanup happens in the catch(AbortError) handler
    }
  }

  // Start new chat from welcome screen — used by playbooks, landing widgets, KB
  // Fixes race where createSession() wasn't awaited: the old 100ms setTimeout
  // could fire before currentSessionId was set in React state, causing
  // handleSendMessage to create a SECOND session (message went to #2, UI
  // showed #1). We now await createSession and pass the ID directly.
  const handleStartChat = async (initialMessage) => {
    try {
      const newSessionId = await createSession()
      if (!newSessionId) {
        console.error('handleStartChat: createSession returned no ID')
        return
      }
      // Brief pause to let React propagate setCurrentSessionId before we send
      await new Promise(resolve => setTimeout(resolve, 30))
      await handleSendMessage(initialMessage, [], newSessionId)
    } catch (err) {
      console.error('handleStartChat failed:', err)
    }
  }

  // KB "Try it" bridge — pick up prompt from Knowledge Base page navigation
  useEffect(() => {
    const kbPrompt = sessionStorage.getItem('kb_try_prompt')
    if (kbPrompt) {
      sessionStorage.removeItem('kb_try_prompt')
      // Small delay to ensure component is mounted
      setTimeout(() => handleStartChat(kbPrompt), 200)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Header new chat — background stream keeps running, just create new session
  const handleHeaderNewChat = () => {
    createSession()
  }

  return (
    <div className="h-screen flex overflow-hidden bg-white dark:bg-surface-dark">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isLoading={isLoading}
        onCancelRequest={handleCancel}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => {
          const next = !sidebarCollapsed
          setSidebarCollapsed(next)
          try { localStorage.setItem('trendy_sidebar_collapsed', String(next)) } catch {}
        }}
        onStartChat={handleStartChat}
      />

      {/* Overlay for mobile */}
      {sidebarOpen && !sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header - sticky on mobile */}
        <header className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-surface-dark sticky top-0 z-20">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay active:bg-gray-200 dark:active:bg-gray-700 rounded-lg transition-colors touch-manipulation flex-shrink-0"
            >
              <Menu size={22} className="text-gray-600 dark:text-gray-400 sm:w-5 sm:h-5" />
            </button>
            <h1 className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base truncate">
              {currentSession?.title || 'Trendy AI Assistant'}
            </h1>
          </div>

          {/* Width toggle — wide mode gives tables room to breathe */}
          <button
            onClick={toggleChatWidth}
            className={`p-2.5 sm:p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
              chatWideMode
                ? 'bg-amtrend-blue/10 text-amtrend-blue'
                : 'hover:bg-gray-100 dark:hover:bg-surface-dark-overlay active:bg-gray-200 dark:active:bg-gray-700'
            }`}
            title={chatWideMode ? 'Standard width' : 'Wide view — more room for tables'}
          >
            <Maximize2 size={22} className={`sm:w-5 sm:h-5 ${chatWideMode ? '' : 'text-gray-600 dark:text-gray-400'}`} />
          </button>

          {/* Dark mode toggle */}
          <button
            onClick={() => {
              const next = theme === 'system' ? 'dark' : theme === 'dark' ? 'light' : 'system'
              setTheme(next)
            }}
            className={`p-2.5 sm:p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
              theme !== 'system'
                ? 'bg-amtrend-blue/10 text-amtrend-blue'
                : 'hover:bg-gray-100 dark:hover:bg-surface-dark-overlay active:bg-gray-200 dark:active:bg-gray-700'
            }`}
            title={
              theme === 'system' ? 'Theme: System (click for dark)'
              : theme === 'dark' ? 'Theme: Dark (click for light)'
              : 'Theme: Light (click for system)'
            }
          >
            {theme === 'system' ? (
              <Monitor size={22} className={`sm:w-5 sm:h-5 ${theme === 'system' ? (isDark ? 'text-gray-400' : 'text-gray-600') : ''}`} />
            ) : isDark ? (
              <Moon size={22} className="sm:w-5 sm:h-5" />
            ) : (
              <Sun size={22} className="sm:w-5 sm:h-5" />
            )}
          </button>

          {/* Settings gear */}
          <button
            onClick={() => navigate('/settings')}
            className="p-2.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay active:bg-gray-200 dark:active:bg-gray-700 rounded-lg transition-colors touch-manipulation flex-shrink-0"
            title="Settings"
          >
            <Settings size={22} className="text-gray-600 dark:text-gray-400 sm:w-5 sm:h-5" />
          </button>

          {/* Keyboard shortcuts — desktop only */}
          <div className="relative hidden sm:block">
            <button
              onClick={() => setShowShortcuts(!showShortcuts)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay active:bg-gray-200 dark:active:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              title="Keyboard shortcuts"
            >
              <Command size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
            {showShortcuts && (
              <ShortcutsPopover onClose={() => setShowShortcuts(false)} />
            )}
          </div>

          <button
            onClick={handleHeaderNewChat}
            className="p-2.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay active:bg-gray-200 dark:active:bg-gray-700 rounded-lg transition-colors touch-manipulation flex-shrink-0"
            title="New chat"
          >
            <MessageSquarePlus size={22} className="text-gray-600 dark:text-gray-400 sm:w-5 sm:h-5" />
          </button>
        </header>

        {/* Chat Area + Artifact Panel Container */}
        <div className="flex-1 flex overflow-hidden">
          {/* Chat Messages Area */}
          <div className={`relative flex-1 flex flex-col min-w-0 ${artifacts.length > 0 ? 'lg:flex-[2]' : ''}`}>
            <M365ReconnectBanner />
            <div
              ref={scrollContainerRef}
              onScroll={handleMessagesScroll}
              className="flex-1 overflow-y-auto"
            >
              {!currentSessionId ? (
                <LandingPage user={user} onStartChat={handleStartChat} />
              ) : messages.length === 0 ? (
                <LandingPage user={user} onStartChat={handleSendMessage} />
              ) : (
                <>
                  {messages.map((message, index) => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      isLast={index === messages.length - 1 && !isLoading}
                      onSendMessage={(msg) => handleSendMessage(msg)}
                      sessionId={currentSessionId}
                      maxWidthClass={maxWidthClass}
                    />
                  ))}
                  {isLoading && (
                    <ErrorBoundary>
                      <StreamingMessage
                        content={streamingContent}
                        activeTools={activeTools}
                        hasAttachments={hasAttachments}
                        maxWidthClass={maxWidthClass}
                      />
                    </ErrorBoundary>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* "Jump to latest" pill — appears only when the user has scrolled
                up from the bottom, so following is never forced on them. */}
            {showScrollButton && (
              <button
                onClick={scrollToBottom}
                className="absolute left-1/2 -translate-x-1/2 bottom-4 z-10 flex items-center gap-1.5 pl-3 pr-3.5 py-2 rounded-full bg-white dark:bg-surface-dark-overlay border border-gray-200 dark:border-gray-700 shadow-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Scroll to latest"
              >
                <ChevronDown size={16} />
                Scroll to latest
              </button>
            )}

            {/* Input */}
            <ChatInput
              onSend={handleSendMessage}
              isLoading={isLoading}
              onCancel={handleCancel}
              maxWidthClass={maxWidthClass}
            />
          </div>

          {/* Artifact Panel - Photo Gallery, etc. (like Claude.ai) */}
          <div
            className="transition-all duration-500 ease-out overflow-hidden"
            style={{ width: artifacts.length > 0 ? undefined : 0, opacity: artifacts.length > 0 ? 1 : 0 }}
          >
          {artifacts.length > 0 && (
            <ArtifactPanel
              artifact={artifacts[activeArtifactIndex]}
              artifacts={artifacts}
              activeIndex={activeArtifactIndex}
              onSelectArtifact={(index) => {
                setActiveArtifactIndex(index)
                // Persist the active index change
                if (currentSessionId) {
                  saveArtifactsForSession(currentSessionId, artifacts, index)
                }
              }}
              onCloseArtifact={(index) => {
                // Remove specific artifact from array
                setArtifacts(prev => {
                  const newArtifacts = prev.filter((_, i) => i !== index)
                  // Adjust active index if needed
                  const newIndex = index >= newArtifacts.length
                    ? Math.max(0, newArtifacts.length - 1)
                    : index
                  setActiveArtifactIndex(newIndex)
                  // Persist the change
                  if (currentSessionId) {
                    if (newArtifacts.length > 0) {
                      saveArtifactsForSession(currentSessionId, newArtifacts, newIndex)
                    } else {
                      clearArtifactsForSession(currentSessionId)
                    }
                  }
                  return newArtifacts
                })
              }}
              onUpdateArtifact={(index, updates) => {
                // Merge updates into a specific artifact and persist.
                // Used by email_compose to write back status='sent' + the user's
                // final edited values so the artifact becomes the audit record.
                setArtifacts(prev => {
                  const next = prev.map((a, i) => (i === index ? { ...a, ...updates } : a))
                  if (currentSessionId) {
                    saveArtifactsForSession(currentSessionId, next, activeArtifactIndex)
                  }
                  return next
                })
              }}
              getArtifactLabel={getArtifactLabel}
              isMinimized={artifactMinimized}
              onMinimize={() => setArtifactMinimized(!artifactMinimized)}
              onClose={() => {
                setArtifacts([])
                setActiveArtifactIndex(0)
                setArtifactMinimized(false)
                // Also clear from localStorage when user explicitly closes all
                if (currentSessionId) {
                  clearArtifactsForSession(currentSessionId)
                }
              }}
              onEmailRequest={(message) => handleSendMessage(message)}
            />
          )}
          </div>

          {/* Right-side source viewer — PR1 onward. Renders nothing when no
              sources are open, so the chat reclaims the full width. */}
          <SourcePane />
        </div>
      </div>

      {/* Search Modal (Cmd/Ctrl+K) */}
      <SearchModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        onSelectSession={selectSession}
      />

      {/* Write Tool Approval Modal */}
      {pendingApproval && (
        <WriteApprovalModal
          approvalId={pendingApproval.approvalId}
          toolName={pendingApproval.toolName}
          toolInput={pendingApproval.toolInput}
          onClose={(approved) => setPendingApproval(null)}
        />
      )}
    </div>
  )
}

// Outer wrapper — provides the right-pane SourcePane context to the whole
// chat surface. PR4 will also mount the Action Card UI through this provider.
export default function ChatApp() {
  return (
    <SourcePaneProvider>
      <ChatAppInner />
    </SourcePaneProvider>
  )
}
